import React, { useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  MenubarContext,
  ParentMenuContext
} from '../../../../components/Menubar/contexts';
import { addHelperFile } from '../../actions/helpers';
import NavFlyout from './NavFlyout';

/**
 * "Add Helpers…": a Sketch-menu flyout that drops one of the injected helper files into the
 * sketch — skyTransforms.js (celestial math + frame ops), skyDraw.js (star/constellation
 * drawing), or arSlider.js (createArSlider()/resetArSliders() registry synced to AR). Each is
 * added once; picking one that already exists is a no-op (addHelperFile checks by filename).
 * Lives inside the Sketch submenu, so it borrows the Menubar's focus/blur handlers the same way
 * MyClassroomMenu does, or the parent menu closes as soon as the flyout opens.
 */
const AddHelpersMenu = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setMenuOpen, createMenuItemHandlers } = useContext(MenubarContext);
  const parentMenu = useContext(ParentMenuContext);

  const itemHandlers = createMenuItemHandlers
    ? createMenuItemHandlers(parentMenu)
    : {};
  const triggerProps = {
    onFocus: itemHandlers.onFocus,
    onBlur: itemHandlers.onBlur
  };

  const add = (kind) => {
    setMenuOpen('none');
    dispatch(addHelperFile(kind));
  };

  const options = [
    {
      kind: 'transforms',
      label: t('Nav.Sketch.HelperTransforms', {
        defaultValue: 'Sky math & transforms'
      })
    },
    {
      kind: 'draw',
      label: t('Nav.Sketch.HelperDraw', {
        defaultValue: 'Star & constellation drawing'
      })
    },
    {
      kind: 'arSlider',
      label: t('Nav.Sketch.HelperArSlider', {
        defaultValue: 'AR Sliders (createArSlider)'
      })
    }
  ];

  return (
    <NavFlyout
      label={t('Nav.Sketch.AddHelpers', { defaultValue: 'Add Helpers…' })}
      triggerProps={triggerProps}
    >
      {options.map(({ kind, label }) => (
        <li key={kind} className="nav__dropdown-item">
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            // Prevent the default left-mousedown so focus stays on the flyout trigger — otherwise
            // its blur fires the Menubar's ~10ms hide-timeout and this button unmounts before the
            // click lands (same fix as the sketch links in ClassroomStudentsMenu).
            onMouseDown={(e) => {
              if (
                e.button === 0 &&
                !e.metaKey &&
                !e.ctrlKey &&
                !e.shiftKey &&
                !e.altKey
              ) {
                e.preventDefault();
              }
            }}
            onClick={() => add(kind)}
          >
            {label}
          </button>
        </li>
      ))}
    </NavFlyout>
  );
};

export default AddHelpersMenu;
